/**
 * Explored cells (spec 13): slippy-map tile maths shared by API, web and mobile.
 *
 * A "cell" is a Web Mercator tile at EXPLORED_CELL_ZOOM. Cell ids are the tile
 * x/y integers. The database computes the same mapping in SQL
 * (`packages/db/src/matching/explored-cells-sql.ts`) - the two must agree, so
 * change both together or not at all.
 */

export const EXPLORED_CELL_ZOOM = 15;

const N = 2 ** EXPLORED_CELL_ZOOM;
const MAX_LAT = 85.05112878;

export interface ExploredCell {
  x: number;
  y: number;
}

/** Fractional tile coordinates for a lng/lat at EXPLORED_CELL_ZOOM. */
export function lngLatToTileFrac(lng: number, lat: number): { x: number; y: number } {
  const clampedLat = Math.max(-MAX_LAT, Math.min(MAX_LAT, lat));
  const rad = (clampedLat * Math.PI) / 180;
  const x = ((lng + 180) / 360) * N;
  const y = ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * N;
  return { x, y };
}

export function lngLatToCell(lng: number, lat: number): ExploredCell {
  const { x, y } = lngLatToTileFrac(lng, lat);
  return { x: Math.floor(x), y: Math.floor(y) };
}

/** [west, south, east, north] bounds of a cell. */
export function cellToBounds(cell: ExploredCell): [number, number, number, number] {
  const west = (cell.x / N) * 360 - 180;
  const east = ((cell.x + 1) / N) * 360 - 180;
  const north = tileYToLat(cell.y);
  const south = tileYToLat(cell.y + 1);
  return [west, south, east, north];
}

function tileYToLat(y: number): number {
  const n = Math.PI - (2 * Math.PI * y) / N;
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

/** Stable integer key for Set/Map dedupe. */
export function cellKey(cell: ExploredCell): number {
  return cell.x * N + cell.y;
}

export function cellFromKey(key: number): ExploredCell {
  return { x: Math.floor(key / N), y: key % N };
}

/**
 * Decode the wire format used by GET /map/explored: a flat [x0,y0,x1,y1,...]
 * integer array.
 */
export function decodeCellArray(flat: number[]): ExploredCell[] {
  const out: ExploredCell[] = new Array(Math.floor(flat.length / 2));
  for (let i = 0, j = 0; i + 1 < flat.length; i += 2, j++) {
    out[j] = { x: flat[i]!, y: flat[i + 1]! };
  }
  return out;
}

/** GeoJSON polygon ring for a cell (closed, counter-clockwise). */
export function cellToRing(cell: ExploredCell): [number, number][] {
  const [w, s, e, n] = cellToBounds(cell);
  return [
    [w, s],
    [e, s],
    [e, n],
    [w, n],
    [w, s],
  ];
}
